import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Bell, 
  CheckCircle, 
  TrendingUp, 
  TrendingDown,
  FileText,
  Mail,
  AlertCircle,
  Check,
  CheckCheck
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const notificationIcons = {
  position_change: TrendingUp,
  position_drop: TrendingDown,
  report_ready: FileText,
  new_lead: Mail,
  info: Bell,
  alert: AlertCircle
};

const notificationColors = {
  position_change: 'bg-green-50 text-green-600',
  position_drop: 'bg-red-50 text-red-500',
  report_ready: 'bg-blue-50 text-blue-600',
  new_lead: 'bg-purple-50 text-purple-600',
  info: 'bg-neutral-100 text-neutral-600',
  alert: 'bg-yellow-50 text-yellow-600'
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      const params = filter === 'unread' ? '?unread_only=true' : '';
      const { data } = await axios.get(`${API}/notifications${params}`, { withCredentials: true });
      setNotifications(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Layout>
      <div className="space-y-6" data-testid="notifications-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Notifications
            </h1>
            <p className="text-neutral-500 mt-1">
              {unreadCount > 0 ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}` : 'Toutes les notifications sont lues'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex border border-neutral-200 rounded overflow-hidden">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-[#002FA7] text-white' 
                    : 'bg-white text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                Toutes
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  filter === 'unread' 
                    ? 'bg-[#002FA7] text-white' 
                    : 'bg-white text-neutral-600 hover:bg-neutral-50'
                }`}
              >
                Non lues
              </button>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                onClick={markAllAsRead}
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Tout marquer lu
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white border border-neutral-200">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-notifications">
              <Bell className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-500">Aucune notification</p>
              <p className="text-sm text-neutral-400 mt-1">
                Les alertes de changement de position apparaîtront ici
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100" data-testid="notifications-list">
              {notifications.map((notification) => {
                const IconComponent = notificationIcons[notification.notification_type] || Bell;
                const colorClass = notificationColors[notification.notification_type] || notificationColors.info;
                
                return (
                  <div 
                    key={notification.id} 
                    className={`p-4 hover:bg-neutral-50 transition-colors ${!notification.is_read ? 'bg-blue-50/30' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className={`font-medium ${!notification.is_read ? 'text-neutral-900' : 'text-neutral-700'}`}>
                              {notification.title}
                            </p>
                            <p className="text-sm text-neutral-500 mt-1">
                              {notification.message}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!notification.is_read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1 hover:bg-neutral-200 rounded"
                                title="Marquer comme lu"
                              >
                                <Check className="w-4 h-4 text-neutral-500" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="font-mono text-xs text-neutral-400 mt-2">
                          {notification.created_at && format(new Date(notification.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
